import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { X } from "lucide-react";

const GOLD = "#D4AF37";
const RED = "#ef4444";

function InternalDialog({
  mode = "confirm",
  title,
  message,
  defaultValue = "",
  confirmLabel,
  cancelLabel = "Cancelar",
  danger = false,
  onResolve,
}) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);
  const isPrompt = mode === "prompt";

  useEffect(() => {
    if (!isPrompt) return undefined;
    const id = requestAnimationFrame(() => inputRef.current?.select());
    return () => cancelAnimationFrame(id);
  }, [isPrompt]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onResolve(null);
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [onResolve]);

  const submit = () => onResolve(isPrompt ? value : true);
  const cancel = () => onResolve(null);

  return (
    <div
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) cancel();
      }}
      style={styles.overlay}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        style={styles.dialog}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={styles.header}>
          <h2 id="app-dialog-title" style={styles.title}>
            {title || (isPrompt ? "Ingresar dato" : "Confirmar")}
          </h2>
          <button type="button" onClick={cancel} aria-label="Cerrar" style={styles.closeButton}>
            <X size={18} />
          </button>
        </div>

        {message ? <p style={styles.message}>{message}</p> : null}

        {isPrompt ? (
          <input
            ref={inputRef}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                submit();
              }
            }}
            style={styles.input}
          />
        ) : null}

        <div style={styles.actions}>
          <button type="button" onClick={cancel} style={styles.cancelButton}>
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={submit}
            style={{ ...styles.confirmButton, ...(danger ? styles.dangerButton : null) }}
          >
            {confirmLabel || (isPrompt ? "Aceptar" : "Confirmar")}
          </button>
        </div>
      </section>
    </div>
  );
}

function mountDialog(options) {
  if (typeof document === "undefined") {
    return Promise.resolve(options.mode === "prompt" ? null : false);
  }

  return new Promise((resolve) => {
    const host = document.createElement("div");
    host.dataset.appDialogHost = "true";
    document.body.appendChild(host);
    const root = createRoot(host);
    let settled = false;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      root.unmount();
      host.remove();
      resolve(result);
    };

    root.render(<InternalDialog {...options} onResolve={finish} />);
  });
}

let dialogQueue = Promise.resolve();

function enqueueDialog(options) {
  const run = dialogQueue.then(
    () => mountDialog(options),
    () => mountDialog(options),
  );
  dialogQueue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

export function confirmInApp(options = {}) {
  const normalized = typeof options === "string" ? { message: options } : options;
  return enqueueDialog({ ...normalized, mode: "confirm" }).then(Boolean);
}

export function promptInApp(options = {}) {
  const normalized = typeof options === "string" ? { title: options } : options;
  return enqueueDialog({ ...normalized, mode: "prompt" });
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 100000,
    display: "grid",
    placeItems: "center",
    padding: 16,
    background: "rgba(5, 4, 3, 0.72)",
    backdropFilter: "blur(8px)",
  },
  dialog: {
    width: "min(440px, calc(100vw - 32px))",
    maxHeight: "calc(100vh - 32px)",
    overflow: "auto",
    padding: 18,
    borderRadius: 18,
    border: "1px solid rgba(212, 175, 55, 0.32)",
    background: "rgba(24, 21, 18, 0.97)",
    boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
    color: "#f5f1e8",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  title: {
    margin: 0,
    fontSize: "1rem",
    fontWeight: 700,
  },
  message: {
    margin: "12px 0 0",
    lineHeight: 1.45,
    color: "rgba(245,241,232,0.78)",
    fontSize: "0.9rem",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    marginTop: 14,
    padding: "11px 12px",
    borderRadius: 11,
    border: "1px solid rgba(212,175,55,0.3)",
    outline: "none",
    background: "rgba(255,255,255,0.06)",
    color: "#fff",
    font: "inherit",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 9,
    marginTop: 18,
  },
  closeButton: {
    width: 36,
    height: 36,
    display: "grid",
    placeItems: "center",
    border: "none",
    borderRadius: 10,
    background: "transparent",
    color: "rgba(255,255,255,0.62)",
    cursor: "pointer",
  },
  cancelButton: {
    minHeight: 42,
    padding: "0 15px",
    borderRadius: 11,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.05)",
    color: "#f5f1e8",
    cursor: "pointer",
  },
  confirmButton: {
    minHeight: 42,
    padding: "0 15px",
    borderRadius: 11,
    border: "1px solid rgba(212,175,55,0.45)",
    background: "rgba(212,175,55,0.16)",
    color: GOLD,
    fontWeight: 700,
    cursor: "pointer",
  },
  dangerButton: {
    borderColor: "rgba(239,68,68,0.55)",
    background: "rgba(239,68,68,0.14)",
    color: RED,
  },
};
